<!-- Certificate Report - Professional Design -->
<div class="certificate-report-container">
  <!-- Page Header -->
  <header class="report-header">
    <div class="header-content">
      <h1 class="page-title">
        <i class="fa fa-file-text-o title-icon" aria-hidden="true"></i>
        Package Report
      </h1>
      <p class="page-subtitle">Comprehensive package analytics and certificate management</p>
    </div>
  </header>

  <!-- Search Filters Card -->
  <section class="filters-card" aria-label="Search filters">
    <div class="card-header">
      <h2 class="card-title">
        <i class="fa fa-filter" aria-hidden="true"></i>
        Search Filters
      </h2>
    </div>
    
    <form [formGroup]="certificateReportForm" (ngSubmit)="onSubmit()" class="filters-form">
      <div class="form-grid">
        <!-- Row 1 -->
        <div class="form-group">
          <label for="packageNumber" class="form-label">Package Number</label>
          <input 
            type="text" 
            id="packageNumber"
            formControlName="packageNumber" 
            class="form-input" 
            placeholder="Enter package number"
            aria-label="Package number"
            [ngClass]="{ 'is-invalid': submitted && f.packageNumber.errors }" />
        </div>
        
        <div class="form-group">
          <label for="policyNumber" class="form-label">Policy Number</label>
          <input 
            type="text" 
            id="policyNumber"
            formControlName="policyNumber" 
            class="form-input" 
            placeholder="Enter policy number"
            aria-label="Policy number"
            [ngClass]="{ 'is-invalid': submitted && f.policyNumber.errors }" />
        </div>
        
        <div class="form-group">
          <label for="firstName" class="form-label">Client First Name</label>
          <input 
            type="text" 
            id="firstName"
            formControlName="firstName" 
            class="form-input" 
            placeholder="Enter first name"
            aria-label="Client first name"
            [ngClass]="{ 'is-invalid': submitted && f.firstName.errors }" />
        </div>
        
        <!-- Row 2 -->
        <div class="form-group">
          <label for="lastName" class="form-label">Client Last Name</label>
          <input 
            type="text" 
            id="lastName"
            formControlName="lastName" 
            class="form-input" 
            placeholder="Enter last name"
            aria-label="Client last name"
            [ngClass]="{ 'is-invalid': submitted && f.lastName.errors }" />
        </div>
        
        <div class="form-group">
          <label for="userName" class="form-label">Executive User Name</label>
          <input 
            type="text" 
            id="userName"
            formControlName="userName" 
            class="form-input" 
            placeholder="Enter username"
            aria-label="Executive username"
            [ngClass]="{ 'is-invalid': submitted && f.userName.errors }" />
        </div>
        
        <div class="form-group" *ngIf='user.role == "admin"'>
          <label for="dealerId" class="form-label">Dealer ID</label>
          <input 
            type="text" 
            id="dealerId"
            formControlName="dealerId" 
            class="form-input" 
            placeholder="Enter dealer ID"
            aria-label="Dealer ID"
            [ngClass]="{ 'is-invalid': submitted && f.dealerId.errors }" />
        </div>
        
        <!-- Row 3 -->
        <div class="form-group">
          <label for="issueDateFrom" class="form-label">Issue Date From</label>
          <div class="date-input-group">
            <input 
              id="issueDateFrom"
              class="form-input" 
              formControlName="issueDateFrom" 
              ngbDatepicker 
              #d="ngbDatepicker" 
              placeholder="Select date"
              aria-label="Issue date from"
              [ngClass]="{ 'is-invalid': submitted && f.issueDateFrom.errors }"
              [container]="'body'" 
              [placement]="'auto'" 
              [autoClose]="'outside'"
              [displayMonths]="1"
              [navigation]="'select'"
              [dayTemplate]="t"
              [outsideDays]="'hidden'"
              >
            <ng-template #t let-date let-focused="focused">
              <div class="custom-day" [class.focused]="focused">{{ date.day }}</div>
            </ng-template>
            <button 
              class="date-picker-btn" 
              (click)="d.toggle()" 
              type="button"
              aria-label="Open calendar for issue date from">
              <i class="fa fa-calendar" aria-hidden="true"></i>
            </button>
          </div>
          <div *ngIf="submitted && f.issueDateFrom.errors" class="error-message" role="alert">
            <div *ngIf="f.issueDateFrom.errors.required">Issue Date From is required</div>
          </div>
        </div>
        
        <div class="form-group">
          <label for="issueDateTo" class="form-label">Issue Date To</label>
          <div class="date-input-group">
            <input 
              id="issueDateTo"
              class="form-input" 
              formControlName="issueDateTo" 
              ngbDatepicker 
              #issueDateTo="ngbDatepicker" 
              placeholder="Select date"
              aria-label="Issue date to"
              [ngClass]="{ 'is-invalid': submitted && f.issueDateTo.errors }"
              [container]="'body'" 
              [placement]="'auto'" 
              [autoClose]="'outside'"
              [displayMonths]="1"
              [navigation]="'select'"
              [dayTemplate]="t2"
              [outsideDays]="'hidden'"
              >
            <ng-template #t2 let-date let-focused="focused">
              <div class="custom-day" [class.focused]="focused">{{ date.day }}</div>
            </ng-template>
            <button 
              class="date-picker-btn" 
              (click)="issueDateTo.toggle()" 
              type="button"
              aria-label="Open calendar for issue date to">
              <i class="fa fa-calendar" aria-hidden="true"></i>
            </button>
          </div>
          <div *ngIf="submitted && f.issueDateTo.errors" class="error-message" role="alert">
            <div *ngIf="f.issueDateTo.errors.required">Issue Date To is required</div>
          </div>
        </div>
        
        <div class="form-group" *ngIf='user.role == "admin" || user.role == "employee"'>
          <label for="dealerGroupName" class="form-label">Dealer Group Name</label>
          <input 
            type="text" 
            id="dealerGroupName"
            formControlName="dealerGroupName" 
            class="form-input" 
            placeholder="Enter group name"
            aria-label="Dealer group name"
            [ngClass]="{ 'is-invalid': submitted && f.dealerGroupName.errors }" />
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div class="action-buttons" role="toolbar" aria-label="Report actions">
        <button 
          [disabled]="loading" 
          class="btn btn-primary" 
          type="button"
          aria-label="Search reports"
          (click)="onSearch()">
          <span *ngIf="loading" class="spinner" aria-hidden="true"></span>
          <i class="fa fa-search" *ngIf="!loading" aria-hidden="true"></i>
          Search
        </button>
        
        <button 
          class="btn btn-secondary" 
          type="button" 
          aria-label="Reset search form"
          (click)="resetForm()">
          <i class="fa fa-refresh" aria-hidden="true"></i>
          Reset
        </button>
        
        <button 
          (click)="exportToExcel()" 
          class="btn btn-success" 
          type="button"
          aria-label="Export results to Excel"
          [disabled]="!reportCertificateData || reportCertificateData.length === 0">
          <span *ngIf="loading" class="spinner" aria-hidden="true"></span>
          <i class="fa fa-file-excel-o" *ngIf="!loading" aria-hidden="true"></i>
          Export to Excel
        </button>
        
        <button 
          (click)="clearFilters()" 
          class="btn btn-outline-primary" 
          type="button"
          aria-label="Clear grid filters"
          [disabled]="!gridApi">
          <i class="fa fa-filter" aria-hidden="true"></i>
          Clear Filters
        </button>
      </div>
    </form>
  </section>

  <!-- Data Grid Card -->
  <section class="grid-card" aria-label="Search results">
    <div class="card-header">
      <h2 class="card-title">
        <i class="fa fa-table" aria-hidden="true"></i>
        Search Results
        <span class="results-badge" *ngIf="reportCertificateData && reportCertificateData.length > 0">
          {{ reportCertificateData.length }} records
        </span>
      </h2>
    </div>
    
    <div class="grid-container">
      <ag-grid-angular 
        #aggrid
        class="ag-theme-alpine professional-grid"
        [gridOptions]="gridOptions"
        [rowData]="gridOptions.rowData"
        [columnDefs]="gridOptions.columnDefs"
        aria-label="Certificate report data grid"
        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onFirstDataRendered($event)"
        (selectionChanged)="onSelectionChanged()">
      </ag-grid-angular>
    </div>
  </section>
</div>
